/**
 * Axios instance with transparent token refresh.
 *
 * The access token lives in memory only — never localStorage, where any XSS
 * could read it. On a 401 the client silently refreshes using the httpOnly
 * cookie and replays the original request. Concurrent 401s share one refresh
 * promise, so a dashboard firing six requests does not trigger six rotations
 * (which would invalidate each other and log the user out).
 */
import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

const BASE_URL = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '/api';

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Called when refreshing fails — the app uses it to bounce to /login. */
export function setUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn;
}

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  refreshPromise ??= (async () => {
    try {
      const res = await axios.post<{ accessToken: string }>(
        `${BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true, timeout: 15_000 },
      );
      accessToken = res.data.accessToken;
      return accessToken;
    } catch {
      accessToken = null;
      onUnauthenticated?.();
      return null;
    } finally {
      // Cleared in a microtask so every caller awaiting this round gets the
      // same result before a new attempt can start.
      queueMicrotask(() => {
        refreshPromise = null;
      });
    }
  })();

  return refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<ApiErrorBody>) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;

    // Refresh once per request. The auth endpoints are excluded — a failed
    // login must surface as a login failure, not trigger a refresh loop.
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !original.url?.includes('/auth/')
    ) {
      original._retried = true;
      const token = await refreshAccessToken();
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api.request(original);
      }
    }

    const body = error.response?.data;
    if (body?.error) {
      throw new ApiError(
        body.error.code,
        body.error.message,
        error.response?.status ?? 0,
        body.error.details,
      );
    }

    if (error.code === 'ECONNABORTED') {
      throw new ApiError('TIMEOUT', 'Заявката отне твърде дълго', 0);
    }
    if (!error.response) {
      throw new ApiError('NETWORK', 'Няма връзка със сървъра', 0);
    }

    throw new ApiError('UNKNOWN', error.message || 'Неочаквана грешка', error.response.status);
  },
);

/** Restore a session on page load using the refresh cookie. */
export async function bootstrapSession(): Promise<{
  accessToken: string;
  user: { id: number; email: string; name: string };
} | null> {
  try {
    const res = await axios.post<{
      accessToken: string;
      user: { id: number; email: string; name: string };
    }>(`${BASE_URL}/auth/refresh`, {}, { withCredentials: true, timeout: 15_000 });
    accessToken = res.data.accessToken;
    return res.data;
  } catch {
    return null;
  }
}
